import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import supabase from '../../utils/supabaseClient';
import { showConfirm } from '../../utils/sweetAlert';
import logger from '../../utils/logger';
import { parseMetadataAnswers } from '../../utils/liveSession/answerUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Hook for managing session API actions
 * @param {string} sessionId - Session ID
 * @param {Object} setters - Object containing state setters
 * @param {Function} setters.setSession - Set session state
 * @param {Function} setters.setLoading - Set loading state
 * @param {Function} setters.setError - Set error state
 * @param {Function} setters.setCurrentQuestionIndex - Set current question index
 * @param {Function} setters.setCurrentAnswers - Set current answers
 * @param {Function} setters.setEndingSession - Set ending session state
 * @param {Function} setters.setCompletingSession - Set completing session state
 * @param {Function} setters.setSessionSummary - Set session summary
 * @param {Function} setters.setShowSummary - Set show summary state
 * @returns {Object} Session action functions
 */
export const useSessionActions = ({
  sessionId,
  setSession,
  setLoading,
  setError,
  setCurrentQuestionIndex,
  setCurrentAnswers,
  setEndingSession,
  setCompletingSession,
  setSessionSummary,
  setShowSummary
}) => {
  const navigate = useNavigate();
  
  // Refs to store current state for completion handler
  const currentAnswersRef = useRef({});
  const submitAnswerRef = useRef(null);
  const currentQuestionIndexRef = useRef(0);
  const sessionRef = useRef(null);

  // Fetch session data and existing answers
  const fetchSession = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      const response = await axios.get(
        `${API_URL}/api/live-session/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        const sessionData = response.data.session;
        
        // Update refs for completion handler
        sessionRef.current = sessionData;
        
        // Use functional updates to get current state
        setSession(prevSession => {
          setCurrentQuestionIndex(prevIndex => {
            // Initialize question index from session
            if (sessionData.current_question_index !== undefined) {
              const newIndex = sessionData.current_question_index;
              // Only update if changed (to avoid unnecessary re-renders)
              if (prevIndex !== newIndex) {
                logger.log(`📍 Question index updated: ${prevIndex} → ${newIndex}`);
                currentQuestionIndexRef.current = newIndex;
                return newIndex;
              }
            }
            currentQuestionIndexRef.current = prevIndex;
            return prevIndex;
          });
          
          // Load answers from session questions (answers are already in the session response)
          setCurrentAnswers(prevAnswers => {
            if (sessionData.questions && Array.isArray(sessionData.questions)) {
              const answersMap = {};
              sessionData.questions.forEach(question => {
                if (question.answer) {
                  answersMap[question.id] = {
                    question_id: question.id,
                    answer: question.answer.answer,
                    answer_numeric: question.answer.answer_numeric,
                    answer_boolean: question.answer.answer_boolean,
                    answer_json: question.answer.answer_json,
                    notes: question.answer.notes
                  };
                }
              });
              
              // Only update if answers have changed
              const answersChanged = JSON.stringify(prevAnswers) !== JSON.stringify(answersMap);
              if (answersChanged) {
                if (Object.keys(answersMap).length > 0) {
                  logger.log(`✅ Loaded ${Object.keys(answersMap).length} answers from session response`);
                  currentAnswersRef.current = answersMap;
                  return answersMap;
                } else if (Object.keys(prevAnswers).length > 0) {
                  // Clear answers if session has no answers
                  currentAnswersRef.current = {};
                  return {};
                }
              }
              currentAnswersRef.current = prevAnswers;
            }
            return prevAnswers;
          });
          
          return sessionData;
        });
        
        setError('');
      }
    } catch (err) {
      logger.error('Error fetching session:', err);
      setError(err.response?.data?.detail || 'Failed to load session');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [sessionId, setSession, setLoading, setError, setCurrentQuestionIndex, setCurrentAnswers]);

  // Handle session end (consultant only)
  const handleEndSession = useCallback(async () => {
    const confirmed = await showConfirm(
      'End Session?',
      'Are you sure you want to end this session? This action cannot be undone.',
      { confirmButtonText: 'Yes, end it' }
    );
    if (!confirmed) {
      return;
    }

    try {
      setEndingSession(true);

      const response = await axios.patch(
        `${API_URL}/api/live-session/${sessionId}`,
        { status: 'completed', ended_at: new Date().toISOString() },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success('Session ended successfully!');
        
        // Navigate back after a short delay
        setTimeout(() => {
          navigate('/consultant/dashboard');
        }, 2000);
      }
    } catch (err) {
      logger.error('Error ending session:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to end session';
      toast.error(errorMessage);
    } finally {
      setEndingSession(false);
    }
  }, [sessionId, setEndingSession, navigate]);

  // Handle session cancel (consultant only)
  const handleCancelSession = useCallback(async () => {
    const confirmed = await showConfirm(
      'Cancel Session?',
      'Are you sure you want to cancel this session?',
      { confirmButtonText: 'Yes, cancel it' }
    );
    if (!confirmed) {
      return;
    }

    try {
      setEndingSession(true);

      const response = await axios.patch(
        `${API_URL}/api/live-session/${sessionId}`,
        { status: 'cancelled', ended_at: new Date().toISOString() },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success('Session cancelled');
        
        setTimeout(() => {
          navigate('/consultant/dashboard');
        }, 2000);
      }
    } catch (err) {
      logger.error('Error cancelling session:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to cancel session';
      toast.error(errorMessage);
    } finally {
      setEndingSession(false);
    }
  }, [sessionId, setEndingSession, navigate]);

  // Fetch session summary (defined before handleCompleteSession to avoid initialization error)
  const fetchSessionSummary = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/live-session/${sessionId}/summary`,
        { withCredentials: true }
      );

      if (response.data.success) {
        setSessionSummary(response.data.summary);
      }
    } catch (err) {
      // Don't show error - summary is optional, assessment is already completed
      logger.warn('Summary not available (optional feature):', err.response?.status || err.message);
      // Set a basic summary from session data if available
      // This allows the UI to still work without the summary endpoint
    }
  }, [sessionId, setSessionSummary]);

  // Handle complete session (user only - consultants are view-only)
  const handleCompleteSession = useCallback(async (isUser = false) => {
    // Consultants cannot complete - they are view-only
    if (!isUser) {
      toast.error('Consultants are view-only. Only the user can complete the assessment.', { duration: 4000 });
      return;
    }
    
    // For users, complete the session
    const confirmed = await showConfirm(
      'Complete Assessment?',
      'Complete this assessment session? This will finalize all answers and generate a summary.',
      { confirmButtonText: 'Yes, complete it' }
    );
    if (!confirmed) {
      return;
    }

    try {
      setCompletingSession(true);

      // CRITICAL: Save the current answer before completing (if on the last question)
      // Use refs to get current state without needing them as parameters
      const session = sessionRef.current;
      const currentQuestionIndex = currentQuestionIndexRef.current;
      const currentAnswers = currentAnswersRef.current;
      const submitAnswer = submitAnswerRef.current;
      
      if (session && session.questions && currentQuestionIndex < session.questions.length) {
        const currentQuestion = session.questions[currentQuestionIndex];
        if (currentQuestion && currentAnswers[currentQuestion.id] && submitAnswer) {
          // Save the current answer immediately before completing
          logger.log(`💾 Saving last answer before completion: question ${currentQuestion.id}`);
          try {
            await submitAnswer(currentQuestion.id);
            // Wait a moment for the save to complete
            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (err) {
            logger.error('Error saving last answer before completion:', err);
            // Continue anyway - the answer might already be saved
          }
        }
      }

      const response = await axios.post(
        `${API_URL}/api/live-session/${sessionId}/complete`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success('✅ Assessment completed successfully!', { duration: 3000 });
        
        // Fetch and show summary
        fetchSessionSummary();
        setShowSummary(true);
        
        // Refresh session data (don't show loading spinner - we're already showing completion state)
        await fetchSession(false);
      }
    } catch (err) {
      logger.error('Error completing session:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to complete session';
      toast.error(errorMessage);
    } finally {
      setCompletingSession(false);
    }
  }, [sessionId, setCompletingSession, setShowSummary, fetchSessionSummary, fetchSession]);
  
  // Function to update refs (called from parent hook)
  const updateRefs = useCallback((answers, submitAnswerFn, questionIndex, sessionData) => {
    currentAnswersRef.current = answers;
    submitAnswerRef.current = submitAnswerFn;
    currentQuestionIndexRef.current = questionIndex;
    sessionRef.current = sessionData;
  }, []);

  // Handle leave session (user only)
  const handleLeaveSession = useCallback(async () => {
    const confirmed = await showConfirm(
      'Leave Session?',
      'Are you sure you want to leave this session?',
      { confirmButtonText: 'Yes, leave' }
    );
    if (confirmed) {
      toast.success('You have left the session');
      navigate('/dashboard');
    }
  }, [navigate]);

  return {
    fetchSession,
    handleEndSession,
    handleCancelSession,
    handleCompleteSession,
    fetchSessionSummary,
    handleLeaveSession,
    updateRefs
  };
};

